import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import type { SchedulingJobWeekStatusResponse } from '../api/generated';
import { SchedulingJobWeekStatusResponseStatusEnum } from '../api/generated';
import { fetchWeekCalculationStatuses } from '../services/schedulingJobService';
import {
  MAX_POLL_BACKOFF_SKIPPED_TICKS,
  PERMANENT_FETCH_ERROR_STATUS_CODES,
  WEEK_STATUS_POLL_INTERVAL_MS
} from '../constants/schedulingJobConstants';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { useAppDispatch } from '../store/hooks';

/**
 * The per-week statuses the year plan header renders, and enough about how they were obtained for
 * the header to tell "still loading" from "could not be loaded" — which look identical in the data
 * (both have no statuses) but must not look identical on screen.
 */
export interface WeekCalculationStatuses {
  // Keyed by week number. A week that has never been calculated has no entry.
  readonly statusByWeek: ReadonlyMap<number, SchedulingJobWeekStatusResponse>;
  // False only while the first fetch is still in flight, so the header can hold back its status
  // icons instead of flashing "not calculated" on every load and correcting itself a moment later.
  readonly hasAttempted: boolean;
  // True when a fetch has settled but none has ever succeeded for this selection. The header shows
  // every unlocked week as UNKNOWN rather than claiming it was never calculated, and those chips
  // offer `refresh` instead of a recalculation — retrying is what addresses the actual problem, and
  // triggering a solve on a week whose state is unknown could supersede one already running.
  readonly isUnavailable: boolean;
}

export interface UseWeekCalculationStatusesResult {
  readonly statuses: WeekCalculationStatuses;
  readonly markWeekCalculating: (weekNumber: number) => void;
  readonly refresh: () => void;
}

/**
 * A locally triggered week, standing in for the job until the server reports it.
 *
 * `markedAtSeq` is the fetch counter at the moment of the trigger. A fetch that *started later* is
 * guaranteed to see the queued job, because the trigger's POST creates it before responding — so the
 * arrival of such a fetch is what retires the mark. That ordering is what makes this clock-free,
 * which matters: the backend's timestamps are zone-less and not safely comparable to a client clock.
 */
interface OptimisticMark {
  readonly markedAtSeq: number;
  // Stamped once, here, rather than in the render path. Unlike the server's zone-less timestamps
  // this is UTC-suffixed; nothing renders `createdAt` today, so the divergence is latent.
  readonly createdAt: string;
}

// The statuses we hold, tagged with the location and scenario they were fetched for. Keeping them
// together lets the hook *derive* whether they belong to the current selection instead of clearing
// them from an effect, which would cause a cascading render.
interface StatusesState {
  readonly key: string;
  // The newest successful fetch's statuses, or null until one succeeds. Kept separate from the
  // optimistic marks below so a poll can replace server truth without touching local intent.
  readonly serverStatuses: ReadonlyMap<number, SchedulingJobWeekStatusResponse> | null;
  // Weeks the planner triggered but the server has not reported back yet, merged in on read.
  readonly optimisticMarks: ReadonlyMap<number, OptimisticMark>;
  // True once a fetch has settled either way, success or failure.
  readonly hasAttempted: boolean;
}

const EMPTY_STATUSES: ReadonlyMap<number, SchedulingJobWeekStatusResponse> = new Map();

const LOADING_STATUSES: WeekCalculationStatuses = {
  statusByWeek: EMPTY_STATUSES,
  hasAttempted: false,
  isUnavailable: false
};

const statusesKey = (locationId: string, scenarioId: string): string =>
  `${locationId}|${scenarioId}`;

/**
 * Whether two fetches brought the same news, field by field.
 *
 * A poll usually returns exactly what the last one did, and storing an equal-but-new Map would still
 * change the state's identity. That is expensive here: the Gantt subtree is not memoised, so a fresh
 * Map every 15s re-renders every group row and recomputes all 52 weeks' KPIs on an idle page.
 */
const isSameStatuses = (
  previous: ReadonlyMap<number, SchedulingJobWeekStatusResponse>,
  next: ReadonlyMap<number, SchedulingJobWeekStatusResponse>
): boolean => {
  if (previous.size !== next.size) return false;
  for (const [weekNumber, nextJob] of next) {
    const previousJob = previous.get(weekNumber);
    if (
      previousJob?.status !== nextJob.status ||
      previousJob.createdAt !== nextJob.createdAt ||
      previousJob.finishedAt !== nextJob.finishedAt ||
      previousJob.errorMessage !== nextJob.errorMessage
    ) {
      return false;
    }
  }
  return true;
};

// How many ticks to sit out after this many consecutive failures: 0, 1, 3, 7, then hold at the cap.
const backoffSkippedTicks = (consecutiveFailures: number): number =>
  Math.min(2 ** Math.max(consecutiveFailures - 1, 0) - 1, MAX_POLL_BACKOFF_SKIPPED_TICKS);

const emptyStateFor = (key: string): StatusesState => ({
  key,
  serverStatuses: null,
  optimisticMarks: new Map(),
  hasAttempted: false
});

/**
 * Folds a successful fetch into the state we already hold for the same selection.
 *
 * `seq` is the fetch's number, which decides which optimistic marks this response was late enough to
 * know about — see `OptimisticMark`. Returns `base` itself, not an equal copy, when the fetch brought
 * no news, so an idle year plan does not re-render on the poll interval.
 */
const withServerStatuses = (
  base: StatusesState,
  serverStatuses: ReadonlyMap<number, SchedulingJobWeekStatusResponse>,
  seq: number
): StatusesState => {
  // Retire any mark this fetch was late enough to know about; keep the rest.
  const optimisticMarks = new Map(
    [...base.optimisticMarks].filter(([, mark]) => mark.markedAtSeq >= seq)
  );
  const isUnchanged =
    base.hasAttempted &&
    base.serverStatuses !== null &&
    optimisticMarks.size === base.optimisticMarks.size &&
    isSameStatuses(base.serverStatuses, serverStatuses);
  if (isUnchanged) return base;
  return { key: base.key, serverStatuses, optimisticMarks, hasAttempted: true };
};

/**
 * Keeps the year plan's per-week calculation statuses current by polling.
 *
 * A solve gets a five-minute time budget, so these statuses change on the scale of minutes — the
 * poll is a flat interval rather than anything adaptive; see `WEEK_STATUS_POLL_INTERVAL_MS` for how
 * the cadence was chosen. Polling stops while the tab is hidden and refetches immediately on return,
 * since an unattended year plan otherwise generates thousands of pointless requests a day.
 *
 * Failure handling splits by whether retrying can help. A transient failure (5xx, offline) keeps the
 * last known statuses and lets the next poll try again — blanking every week's icon over one dropped
 * request would be worse than a stale one — but consecutive ones back the cadence off geometrically
 * so an unhealthy backend is not hammered. A permanent one (expired session, revoked permission,
 * missing scenario or endpoint) stops the poll and reports once, so the feature fails loudly instead
 * of quietly rendering nothing forever.
 *
 * A poll that brings no news hands back the previous state object unchanged, and a tick is skipped
 * while a request is still outstanding: the Gantt subtree is not memoised, so needless new state is
 * a full re-render plus 52 KPI recomputations.
 */
export function useWeekCalculationStatuses(
  locationId: string | undefined,
  scenarioId: string | undefined
): UseWeekCalculationStatusesResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [state, setState] = useState<StatusesState | null>(null);

  // Monotonic fetch counter: only the newest request may write state, so switching year can never
  // let a slow response for the previous scenario overwrite the current one's statuses.
  const fetchSeqRef = useRef(0);
  // Set by the polling effect so a permanent failure can halt the interval it doesn't own, and a
  // retry can put it back.
  const stopPollingRef = useRef<(() => void) | null>(null);
  const startPollingRef = useRef<(() => void) | null>(null);
  // One report per selection: without this a permanent failure would open the dialog again on every
  // remaining in-flight response.
  const hasReportedErrorRef = useRef(false);
  // True while a request is outstanding, so a tick cannot pile a second one on top of it.
  const isFetchingRef = useRef(false);
  // Consecutive transient failures, and the ticks still to be sat out because of them.
  const consecutiveFailuresRef = useRef(0);
  const skipTicksRef = useRef(0);
  // `t` is only needed on the failure path, and it must not reach `load`'s dependencies: i18next
  // hands back a fresh `t` on some renders, which would give `load` a new identity, restart the
  // polling effect, set state, and render again — an unbounded loop.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(
    (targetLocationId: string, targetScenarioId: string): void => {
      const seq = ++fetchSeqRef.current;
      const key = statusesKey(targetLocationId, targetScenarioId);
      isFetchingRef.current = true;
      fetchWeekCalculationStatuses(targetLocationId, targetScenarioId)
        .then((statuses) => {
          if (fetchSeqRef.current !== seq) return;
          consecutiveFailuresRef.current = 0;
          skipTicksRef.current = 0;
          const serverStatuses = new Map(statuses.map((status) => [status.weekNumber, status]));
          setState((current) =>
            withServerStatuses(
              current?.key === key ? current : emptyStateFor(key),
              serverStatuses,
              seq
            )
          );
        })
        .catch((error: unknown) => {
          if (fetchSeqRef.current !== seq) return;
          setState((current) => {
            const base = current?.key === key ? current : emptyStateFor(key);
            // Keep whatever we last knew — only the "we have tried" flag changes, so a transient
            // failure leaves the icons alone instead of blanking them.
            return { ...base, hasAttempted: true };
          });

          // A non-Axios throw means our own success handler broke (a body that isn't an array, say),
          // not a flaky network. Retrying cannot fix a contract bug, so treat it as permanent rather
          // than silently absorbing it into the transient path.
          const isAxiosError = axios.isAxiosError(error);
          const httpStatus = isAxiosError ? error.response?.status : undefined;
          const isPermanent =
            !isAxiosError ||
            (httpStatus !== undefined && PERMANENT_FETCH_ERROR_STATUS_CODES.includes(httpStatus));
          if (!isPermanent) {
            consecutiveFailuresRef.current += 1;
            skipTicksRef.current = backoffSkippedTicks(consecutiveFailuresRef.current);
            return;
          }

          stopPollingRef.current?.();
          if (hasReportedErrorRef.current) return;
          hasReportedErrorRef.current = true;
          dispatch(showError({ message: tRef.current('gantt-week-status-load-error') }));
        })
        .finally(() => {
          // Only the newest request owns the flag; an overtaken one must not clear it.
          if (fetchSeqRef.current === seq) isFetchingRef.current = false;
        });
    },
    [dispatch]
  );

  /**
   * Tries again after the statuses failed to load, and resumes normal polling.
   *
   * A permanent failure deliberately stops the interval and latches the report, so a retry has to
   * undo both — otherwise it would fetch once and go quiet again, and a second failure would never be
   * reported. The backoff is forgotten too: the planner asking again is a better signal than our
   * count of past failures.
   */
  const refresh = useCallback(() => {
    if (!locationId || !scenarioId) return;
    consecutiveFailuresRef.current = 0;
    skipTicksRef.current = 0;
    hasReportedErrorRef.current = false;
    load(locationId, scenarioId);
    // `startPolling` no-ops when the interval is already running, so this is safe on a healthy poll.
    if (!document.hidden) startPollingRef.current?.();
  }, [locationId, scenarioId, load]);

  useEffect(() => {
    if (!locationId || !scenarioId) return;

    hasReportedErrorRef.current = false;
    consecutiveFailuresRef.current = 0;
    skipTicksRef.current = 0;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = (): void => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        // Skip while a request is still outstanding: on a slow backend — exactly when this matters —
        // overlapping polls pile up and all but the newest are discarded on arrival anyway.
        if (isFetchingRef.current) return;
        if (skipTicksRef.current > 0) {
          skipTicksRef.current -= 1;
          return;
        }
        load(locationId, scenarioId);
      }, WEEK_STATUS_POLL_INTERVAL_MS);
    };

    const stopPolling = (): void => {
      if (intervalId === null) return;
      clearInterval(intervalId);
      intervalId = null;
    };
    stopPollingRef.current = stopPolling;
    startPollingRef.current = startPolling;

    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        stopPolling();
        return;
      }
      // Catch up on whatever changed while we were away before resuming the cadence.
      load(locationId, scenarioId);
      startPolling();
    };

    load(locationId, scenarioId);
    if (!document.hidden) startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      stopPollingRef.current = null;
      startPollingRef.current = null;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [locationId, scenarioId, load]);

  /**
   * Shows the week as calculating straight away after a trigger, instead of leaving the old icon up
   * for as long as a poll interval. The mark is held apart from the server's statuses and merged in
   * on read, so a poll that left before the trigger cannot erase it on arrival.
   */
  const markWeekCalculating = useCallback(
    (weekNumber: number) => {
      if (!locationId || !scenarioId) return;
      const key = statusesKey(locationId, scenarioId);
      setState((current) => {
        // A mark made before the first fetch lands still has to stick, so fall back to an empty
        // state for the current selection rather than dropping it.
        const base = current?.key === key ? current : emptyStateFor(key);
        const optimisticMarks = new Map(base.optimisticMarks);
        optimisticMarks.set(weekNumber, {
          markedAtSeq: fetchSeqRef.current,
          createdAt: new Date().toISOString()
        });
        return { ...base, optimisticMarks };
      });
    },
    [locationId, scenarioId]
  );

  // Statuses count as current only while they match the current selection, so switching year hides
  // the icons until the new scenario's statuses arrive rather than showing the old year's.
  const isCurrent =
    locationId !== undefined &&
    scenarioId !== undefined &&
    state?.key === statusesKey(locationId, scenarioId);

  const statuses = useMemo<WeekCalculationStatuses>(() => {
    if (!isCurrent || !state) return LOADING_STATUSES;

    const { serverStatuses, optimisticMarks, hasAttempted } = state;
    // Unavailability describes the *fetch*, so it stays true even when a locally triggered week can
    // still be shown: those weeks resolve from their mark, every other week resolves to UNKNOWN.
    const isUnavailable = hasAttempted && serverStatuses === null;
    if (optimisticMarks.size === 0) {
      return { statusByWeek: serverStatuses ?? EMPTY_STATUSES, hasAttempted, isUnavailable };
    }

    const statusByWeek = new Map(serverStatuses ?? EMPTY_STATUSES);
    for (const [weekNumber, mark] of optimisticMarks) {
      statusByWeek.set(weekNumber, {
        weekNumber,
        status: SchedulingJobWeekStatusResponseStatusEnum.Waiting,
        createdAt: mark.createdAt
      });
    }
    return { statusByWeek, hasAttempted, isUnavailable };
  }, [isCurrent, state]);

  return { statuses, markWeekCalculating, refresh };
}
